import { TaxIcon } from "@xola/icons";
import { isArray, toArray } from "lodash";
import React from "react";
import { Audit, LineItem, Purchase, PurchaseItem } from "types";
import { Currency } from "../../../Common/Currency";
import { TimelineItem, TimelineItemReason } from "../TimelineItem";
import { getTaxFeeLineItems } from "@/models/PurchaseItem";

interface TaxFeeChangeTimelineItemProps {
    item: PurchaseItem;
    audit: Audit;
    showDate?: boolean;
}

export const TaxFeeChangeTimelineItem = ({ item, audit, showDate = false }: TaxFeeChangeTimelineItemProps) => {
    const [addTax, taxFeeLineItem] = getTaxFee(item, audit);

    if (addTax === null || !taxFeeLineItem) {
        return null;
    }

    return (
        <TimelineItem title="Taxes & Fees" audit={audit} icon={<TaxIcon />} showDate={showDate}>
            <div className="flex flex-col space-y-2">
                <div className="space-x-1">
                    <Currency shouldRemoveTrailingZeroes={false}>{taxFeeLineItem?.amount}</Currency>
                    <span>{taxFeeLineItem.name}</span>
                </div>
                <TimelineItemReason localize>{addTax ? "Added" : "Removed"}</TimelineItemReason>
            </div>
        </TimelineItem>
    );
};

TaxFeeChangeTimelineItem.supportsAudit = (_purchase: Purchase, item: PurchaseItem, audit: Audit) => {
    const [taxFee] = getTaxFee(item, audit);
    return taxFee !== null;
};

const getTaxFee = (item: PurchaseItem, audit: Audit): [boolean | null, LineItem?] => {
    const items = audit.data?.items;
    if (!items) {
        return [null];
    }

    type Item = { lineItems: any; id: ID };
    const purchaseItems: Item[] = isArray(items) ? items : toArray(items);
    const auditItem = purchaseItems.find((auditItem: Item) => auditItem.id === item?.id);

    const taxFeeLineItems = getTaxFeeLineItems(item);

    if (!(auditItem?.lineItems && taxFeeLineItems)) {
        return [null];
    }

    for (const lineItem of taxFeeLineItems) {
        const auditLineItems = isArray(auditItem.lineItems) ? auditItem.lineItems : toArray(auditItem.lineItems);
        const auditLineItem = auditLineItems.find((auditItr: any) => auditItr.id === lineItem.id);
        if (auditLineItem) {
            return [auditLineItem.includeInAmount?.new ?? null, lineItem];
        }
    }

    return [null];
};
